import React, { memo, useEffect, useState } from "react";
import styles from "./styles.module.scss";
import { cls } from "app/utils";
import AudioPlayer from "app/components/AudioPlayer";
import playIcon from "app/images/play-overlay.png";
import Datafile from "app/components/Datafile";
import { fetchMediaById } from "app/actions/media-library-data";
import { useActions } from "app/utils/hooks";
import PostMediaModal from "./PostMediaView/PostMediaModal";
import MulticamVideoBadge from "app/components/MulticamVideoBadge";
import { MediaModel } from "app/models";

interface Props {
  mediaData: MediaModel;
}

const PostMedia = ({ mediaData }: Props) => {
  const mediaActions = useActions({ fetchMediaById });

  const [postMedia, setPostMedia] = useState<MediaModel>(mediaData);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const modalHandler = () => {
    setIsModalOpen((prev) => !prev);
  };

  useEffect(() => {
    if (postMedia?.processed || !postMedia?.id) {
      return undefined;
    }

    const fetchMedia = async () => {
      const res = await mediaActions.fetchMediaById(postMedia.id);
      if (res?.processed) {
        setPostMedia(res);
      }
    };

    const pollingInterval = setInterval(fetchMedia, 5000);

    return () => {
      clearInterval(pollingInterval);
    };
  }, [postMedia?.id, postMedia?.processed, mediaActions]);

  const renderVideoThumbnail = () => {
    if (!postMedia?.processed) {
      return (
        <div className={styles.video_container}>
          <div className="media-sprite media-sprite-md media-sprite-vid" />
        </div>
      );
    }

    return (
      <div className={styles.video_container}>
        <MulticamVideoBadge data={postMedia} />
        <span className={styles.mediaOverlay}>
          <img alt="play" src={playIcon} />
        </span>
        <img
          className={styles.thumbnail}
          src={postMedia?.thumbnails?.default}
          alt="media"
        />
      </div>
    );
  };

  const renderMedia = () => {
    if (postMedia?.type === "image" || postMedia?.type === "video") {
      return (
        <div
          className={styles.preview_container}
          onClick={
            postMedia?.processed ||
            (postMedia?.type === "image" && postMedia?.originalUrl)
              ? modalHandler
              : undefined
          }
        >
          {postMedia?.type === "image" && (
            <img
              className={styles.thumbnail}
              src={postMedia?.originalUrl}
              alt="media"
            />
          )}
          {postMedia?.type === "video" && renderVideoThumbnail()}
        </div>
      );
    }

    if (postMedia?.type === "datafile") {
      return (
        <a
          className={cls(styles.datafile_container)}
          href={postMedia?.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Datafile title={postMedia?.filename} type={postMedia?.extension} />
        </a>
      );
    }

    if (postMedia?.type === "audio") {
      return <AudioPlayer data={postMedia} />;
    }

    return null;
  };

  return (
    <>
      {renderMedia()}
      <PostMediaModal
        onClose={modalHandler}
        isOpen={isModalOpen}
        mediaData={postMedia}
      />
    </>
  );
};

export default memo(PostMedia);
